import React, { useState, useEffect } from 'react';
import { Container, Row, Col, ProgressBar, Button } from 'react-bootstrap';
import { Route, Switch, useRouteMatch, useHistory } from 'react-router-dom';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faList, faFolder } from '@fortawesome/free-solid-svg-icons';
import TourObjectives from './TourObjectives';
import ScopeTimeline from './ScopeTimeline';
import BudgetParameters from './BudgetParameters';
import VenueTechnical from './VenueTechnical';
import LogisticsTransportation from './LogisticsTransportation';
import MarketingPromotion from './MarketingPromotion';
import PersonnelStaffing from './PersonnelStaffing';
import ReviewSubmit from './ReviewSubmit';
import SavedTourPlans from './SavedTourPlans';
import ViewTourPlan from './ViewTourPlan';
import '../common.css';
import { API_BASE_URL } from '../../../service/api';
import { setMockFormData } from './mockFormData'; // Import the mock data setter
import { generateTourPlan } from '../../../service/workflow-api'; // Import the API function

// Main Tour Planner Component
const TourPlanner = () => {
  const [currentStep, setCurrentStep] = useState(0);
  const [formData, setFormData] = useState({});
  const [formError, setFormError] = useState('');
  const history = useHistory();

  // useEffect to set mock form data on component mount
  useEffect(() => {
    // Remove this line or comment it out for normal operation
      // Simulate a delay for setting mock data
    setMockFormData(setFormData);
      // This is where you would typically fetch data from an API)
  }, []); // Empty dependency array ensures this runs only once on mount

  // Step titles for the progress indicator
  const steps = [
    'Tour Concept',
    'Scope & Timeline',
    'Budget',
    'Venue & Technical',
    'Logistics',
    'Marketing',
    'Personnel',
    'Review & Submit'
  ];

  // Handle form field changes
  const handleInputChange = (changes) => {
    setFormData(prevData => ({
      ...prevData,
      ...changes
    }));
  };

  // Handle next step navigation
  const handleNext = () => {
    // Validation could be added here
    setCurrentStep(prevStep => Math.min(prevStep + 1, steps.length - 1));
    setFormError('');
  };

  // Handle previous step navigation
  const handlePrev = () => {
    setCurrentStep(prevStep => Math.max(prevStep - 1, 0));
    setFormError('');
  };

  // Handle clicking on a step label
  const handleStepClick = (stepIndex) => {
    // You may want to add validation logic here before allowing navigation
    // For example, requiring previous steps to be completed first
    setCurrentStep(stepIndex);
    setFormError('');
  };

  // Handle form submission
  const handleSubmit = async (submissionData) => {
    try {
      const response = await generateTourPlan(submissionData);
      
      if (!response || !response.plan_id) {
        throw new Error('Failed to generate tour plan');
      }
      
      // Return the data to the ReviewSubmit component
      return {
        downloadUrl: `/api/tour-plan-download/${response.plan_id}/`,
        viewUrl: `/tour-plans/${response.plan_id}`,
        planId: response.plan_id
      };
    } catch (error) {
      console.error('Error generating tour plan:', error);
      setFormError('Failed to generate tour plan. Please try again.');
      throw error;
    }
  };

  // Calculate progress percentage
  const progressPercentage = ((currentStep) / (steps.length - 1)) * 100;

  // Handle navigation to Saved Tour Plans
  const goToSavedPlans = () => {
    history.push('/ai-workflows/ai-tour-planner/saved');
  };

  // Render the current step component
  const renderStep = () => {
    switch (currentStep) {
      case 0:
        return (
          <TourObjectives 
            formData={formData} 
            onInputChange={handleInputChange} 
            onNext={handleNext}
            error={formError}
          />
        );
      case 1:
        return (
          <ScopeTimeline 
            formData={formData} 
            onInputChange={handleInputChange} 
            onNext={handleNext} 
            onPrev={handlePrev}
            error={formError}
          />
        );
      case 2:
        return (
          <BudgetParameters 
            formData={formData} 
            onInputChange={handleInputChange} 
            onNext={handleNext} 
            onPrev={handlePrev}
            error={formError}
          />
        );
      case 3:
        return (
          <VenueTechnical 
            formData={formData} 
            onInputChange={handleInputChange} 
            onNext={handleNext} 
            onPrev={handlePrev}
            error={formError}
          />
        );
      case 4:
        return (
          <LogisticsTransportation 
            formData={formData} 
            onInputChange={handleInputChange} 
            onNext={handleNext} 
            onPrev={handlePrev}
            error={formError}
          />
        );
      case 5:
        return (
          <MarketingPromotion 
            formData={formData} 
            onInputChange={handleInputChange} 
            onNext={handleNext} 
            onPrev={handlePrev}
            error={formError}
          />
        );
      case 6:
        return (
          <PersonnelStaffing 
            formData={formData} 
            onInputChange={handleInputChange} 
            onNext={handleNext} 
            onPrev={handlePrev}
            error={formError}
          />
        );
      case 7:
        return (
          <ReviewSubmit 
            formData={formData} 
            onInputChange={handleInputChange} 
            onPrev={handlePrev}
            onSubmit={handleSubmit}
            error={formError}
          />
        );
      default:
        return null;
    }
  };

  return (
    <Container fluid className="p-0 dark-container">
      {/* Header with progress indication */}
      <div className="dark-header py-2 glass-bg" style={{ borderRadius: '0 0 12px 12px' }}>
        <Container>
          <Row className="align-items-center mb-1">
            <Col>
              <h2 className="mb-0 h5" style={{ color: '#4D7AFF' }}>World Tour - AI Events Planner</h2>
              <p className="text-light mb-0 small opacity-75">Create a comprehensive tour planning strategy</p>
            </Col>
            <Col xs="auto" className="d-flex align-items-center">
              <Button 
                variant="primary"
                size="sm"
                style={{
                  backgroundColor: 'rgb(125, 62, 241)',
                  borderColor: 'rgb(125, 62, 241)',
                  color: '#fff',
                  borderRadius: '6px',
                  marginRight: '10px',
                  fontWeight: 500
                }}
                onClick={goToSavedPlans}
              >
                <FontAwesomeIcon icon={faFolder} className="me-2" />
                Saved Plans
              </Button>
              <span className="text-light small opacity-75">Step {currentStep + 1} of {steps.length}</span>
            </Col>
          </Row>
          
          {/* Progress Bar */}
          <ProgressBar 
            now={progressPercentage} 
            className="dark-progress-bar" 
            style={{ height: '5px', borderRadius: '3px' }}
          />
          
          {/* Step Labels - Now Clickable */}
          <Row className="mt-1 mb-0">
            {steps.map((step, index) => (
              <Col key={index} 
                   className={`small text-center ${currentStep >= index ? 'text-light' : 'text-muted opacity-50'}`} 
                   style={{ 
                     fontSize: '0.75rem', 
                     cursor: 'pointer',
                     textDecoration: 'none'
                   }}
                   onClick={() => handleStepClick(index)}
                   title={`Go to ${step}`}>
                {step}
              </Col>
            ))}
          </Row>
        </Container>
      </div>
      
      {/* Main Content - Using the same container sizing as the header */}
      <div className="py-3">
        <Container>
          <div className="section-title mb-3" style={{ color: 'white', margin: '0.5rem 0' }}>
            <h3 className="h5 mb-0">{steps[currentStep]}</h3>
          </div>
          {renderStep()}
        </Container>
      </div>
    </Container>
  );
};

// Router Component for AI Tour Planner
const AiTourPlanner = () => {
  const { path } = useRouteMatch();
  
  return (
    <Switch>
      <Route exact path={path} component={TourPlanner} />
      <Route path={`${path}/saved`} component={SavedTourPlans} />
      <Route path={`${path}/:planId`} component={ViewTourPlan} />
    </Switch>
  );
};

export default AiTourPlanner;